import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Palette, CheckCircle, XCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmail = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus("error");
      setErrorMessage("Invalid verification link. Please request a new one.");
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(
        `${API}/auth/verify-email`,
        { token },
        { withCredentials: true }
      );
      
      setUser(response.data);
      setStatus("success");
      toast.success("Email verified successfully!");
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (error) {
      console.error("Verification error:", error);
      setStatus("error");
      setErrorMessage(error.response?.data?.detail || "Verification failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-hero-gradient flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
            <Palette className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-2xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            ColorWonder
          </span>
        </Link>

        <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100 text-center">
          {status === "verifying" && (
            <>
              <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Verifying Your Email...
              </h1>
              <p className="text-slate-600">
                Please wait while we verify your email address.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Email Verified!
              </h1>
              <p className="text-slate-600 mb-6">
                Your account is now active. Redirecting to dashboard...
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="w-full h-12 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Verification Failed
              </h1>
              <p className="text-slate-600 mb-6">
                {errorMessage}
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/login")}
                  className="w-full h-12 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
                >
                  Go to Login
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/signup")}
                  className="w-full h-12 rounded-full"
                >
                  Sign Up Again
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
